import React, { useContext, useEffect, useState } from 'react';
import { Text, Textarea, Button, Switch } from '@mantine/core';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';

// Per-user, per-art "quote" store. Default is private; a per-quote
// "publish" toggle flips the row to is_public = true, which makes it
// visible to anyone viewing THAT user's art-event page. On the public
// /arts/{id} page we still only ever show the viewer's own quotes —
// nobody browses a shared art and sees a feed of other people's notes.
//
// Props:
// - artId          — required.
// - viewerIsOwner  — when true (default) the component fetches /arts/{id}/myQuotes
//                    and renders the add/edit/delete/publish UI.
//                    When false, pass ownerUserId; we fetch public-only
//                    quotes of that user and render read-only.
// - ownerUserId    — only used in the viewerIsOwner=false path.
// - ownerName      — only for the section-header label in read-only mode.
const MyQuotes = ({ artId, viewerIsOwner = true, ownerUserId = null, ownerName = '' }) => {
  const myUser = useContext(MyUserContext);
  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const [quotes, setQuotes] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftPublic, setDraftPublic] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [editPublic, setEditPublic] = useState(false);
  const [saving, setSaving] = useState(false);

  // In owner mode we need a JWT; otherwise bail. The non-owner path
  // is open to anyone (public quotes are, well, public).
  const canUse = viewerIsOwner ? !!myUser : !!ownerUserId;

  useEffect(() => {
    if (!canUse || !artId) return;
    const url = viewerIsOwner
      ? `/arts/${artId}/myQuotes`
      : `/users/${ownerUserId}/arts/${artId}/publicQuotes`;
    axiosInstance.get(url)
      .then(res => setQuotes(res.data || []))
      .catch(() => setQuotes([]));
  }, [artId, viewerIsOwner, ownerUserId, canUse]);

  if (!canUse) return null;

  const t = (ru, en) => (language === 'ru' ? ru : en);

  const submitNew = () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    axiosInstance.post(`/arts/${artId}/myQuotes`, { text, isPublic: draftPublic })
      .then(res => {
        setQuotes([res.data, ...(quotes || [])]);
        setDraft('');
        setDraftPublic(false);
        setAdding(false);
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const startEdit = (q) => {
    setEditingId(q.id);
    setEditDraft(q.text);
    setEditPublic(!!q.isPublic);
  };

  const submitEdit = () => {
    const text = editDraft.trim();
    const id = editingId;
    if (!text || !id) { setEditingId(null); return; }
    setSaving(true);
    axiosInstance.put(`/myQuotes/${id}`, { text, isPublic: editPublic })
      .then(res => {
        setQuotes((quotes || []).map(q => q.id === id ? res.data : q));
        setEditingId(null);
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  // Fast-path toggle from the card itself (no edit mode) — the common
  // action of "I decided to publish this" shouldn't force a full edit.
  const togglePublic = (q) => {
    const prev = quotes || [];
    const next = prev.map(x => x.id === q.id ? { ...x, isPublic: !x.isPublic } : x);
    setQuotes(next);
    axiosInstance.put(`/myQuotes/${q.id}`, { text: q.text, isPublic: !q.isPublic })
      .then(res => setQuotes((quotes || []).map(x => x.id === q.id ? res.data : x)))
      .catch(() => setQuotes(prev));
  };

  const removeQuote = (id) => {
    if (!window.confirm(t('Удалить цитату?', 'Delete this quote?'))) return;
    const prev = quotes || [];
    setQuotes(prev.filter(q => q.id !== id));
    axiosInstance.delete(`/myQuotes/${id}`).catch(() => setQuotes(prev));
  };

  const list = quotes || [];
  const empty = quotes !== null && list.length === 0;

  // In read-only mode: hide the whole section when the owner has
  // published nothing. There's no CTA for a non-owner, so an empty
  // state would just be dead UI.
  if (!viewerIsOwner && empty) {
    return null;
  }

  const sectionTitle = viewerIsOwner
    ? t('Мои цитаты', 'My quotes')
    : ownerName
      ? t(`Цитаты ${ownerName}`, `${ownerName}'s quotes`)
      : t('Цитаты', 'Quotes');

  return (
    <div className="mx-auto w-full max-w-2xl px-4 md:px-6 mt-6">
      <div className="flex items-center gap-3 mb-3" style={{ color: 'var(--mantine-color-dimmed)' }}>
        <div className="flex-1 h-px" style={{ background: 'var(--mantine-color-default-border)' }} />
        <span className="font-semibold text-sm uppercase tracking-wider" style={{ color: 'var(--mantine-color-text)' }}>
          {sectionTitle}
        </span>
        <div className="flex-1 h-px" style={{ background: 'var(--mantine-color-default-border)' }} />
      </div>

      {list.map(q => (
        <div
          key={q.id}
          className="mb-2 p-3 rounded-lg"
          style={{ background: 'var(--app-surface)', border: '1px solid var(--mantine-color-default-border)' }}
        >
          {viewerIsOwner && editingId === q.id ? (
            <>
              <Textarea
                value={editDraft}
                onChange={(e) => setEditDraft(e.currentTarget.value)}
                autosize
                minRows={2}
                maxRows={10}
                autoFocus
                disabled={saving}
              />
              <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                <Switch
                  size="xs"
                  checked={editPublic}
                  onChange={(e) => setEditPublic(e.currentTarget.checked)}
                  label={t('Публично', 'Public')}
                />
                <div className="flex gap-2">
                  <Button size="xs" onClick={submitEdit} loading={saving}>
                    {t('Сохранить', 'Save')}
                  </Button>
                  <Button size="xs" variant="subtle" color="gray" onClick={() => setEditingId(null)}>
                    {t('Отмена', 'Cancel')}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{q.text}</Text>
              <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Text size="xs" c="dimmed">
                    {new Date(q.createdTime).toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </Text>
                  {viewerIsOwner && (
                    // Fast public/private flip right from the card.
                    // Non-owners don't see this — they can't change
                    // another user's publish state.
                    <Switch
                      size="xs"
                      checked={!!q.isPublic}
                      onChange={() => togglePublic(q)}
                      label={q.isPublic ? t('Публично', 'Public') : t('Личное', 'Private')}
                    />
                  )}
                  {!viewerIsOwner && q.isPublic && (
                    // Explicit tag so readers understand this is a
                    // deliberately published quote, not a leak.
                    <Text size="xs" c="dimmed">· {t('опубликовано', 'public')}</Text>
                  )}
                </div>
                {viewerIsOwner && (
                  <div className="flex gap-1">
                    <Button size="compact-xs" variant="subtle" color="gray" onClick={() => startEdit(q)}>
                      {t('Изменить', 'Edit')}
                    </Button>
                    <Button size="compact-xs" variant="subtle" color="red" onClick={() => removeQuote(q.id)}>
                      {t('Удалить', 'Delete')}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ))}

      {viewerIsOwner && (
        adding ? (
          <div
            className="mb-2 p-3 rounded-lg"
            style={{ background: 'var(--app-surface)', border: '1px solid var(--mantine-color-default-border)' }}
          >
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.currentTarget.value)}
              placeholder={t('Вставьте цитату или запишите мысль', 'Paste a quote or write a thought')}
              autosize
              minRows={2}
              maxRows={10}
              autoFocus
              disabled={saving}
            />
            <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
              <Switch
                size="xs"
                checked={draftPublic}
                onChange={(e) => setDraftPublic(e.currentTarget.checked)}
                label={t('Публично', 'Public')}
              />
              <div className="flex gap-2">
                <Button size="xs" onClick={submitNew} loading={saving} disabled={!draft.trim()}>
                  {t('Сохранить', 'Save')}
                </Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => { setAdding(false); setDraft(''); setDraftPublic(false); }}>
                  {t('Отмена', 'Cancel')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => setAdding(true)}
            >
              {empty
                ? t('+ Добавить первую цитату', '+ Add your first quote')
                : t('+ Добавить цитату', '+ Add a quote')}
            </Button>
          </div>
        )
      )}
    </div>
  );
};

export default MyQuotes;
